<template>
    <div>
        <div v-if="chatMessage.options.choices" class="answer-button__container">
            <colour-picker-component :chatMessage="chatMessage" @colourSelected="onSelect($event)"
                @answerChange="onAnswerChange" :disableColours="disableAnswerButtons"
                v-if="chatMessage.options.makeModelChatQuestionType === MakeModelChatQuestionType.Colour" />
            <div class="answer-button__container"
                v-if="chatMessage.options.makeModelChatQuestionType !== MakeModelChatQuestionType.Colour">
                <div class="answer-button__wrapper" v-for="(answer, index) in chatMessage.options.choices" :key="index">
                    <button :disabled="disableAnswerButtons" @click="onSelect(answer)" class="answer-button__button"
                        :class="getButtonClass(index)">
                        <div class="answer-button__text">
                            {{ answer }}
                        </div>
                    </button>
                </div>
            </div>
            <div v-if="chatMessage.options.helpOptions?.helpButton" class="answer-button__wrapper">
                <button :disabled="disableAnswerButtons"
                    @click="chatMessage.options.helpOptions.helpRequested ? onChatBotRequested() : onHelpRequested()"
                    class="answer-button__button answer-button__bottom-button"
                    v-bind:class="{ 'answer-button__selected': showBot || helpButtonSelected }">
                    <div class="answer-button__text" v-if="chatMessage.options.helpOptions.helpRequested">
                        {{ 'Ask me a question?' }}</div>
                    <div class="answer-button__text" v-if="!chatMessage.options.helpOptions.helpRequested">
                        {{ 'Help me choose' }}</div>
                </button>
            </div>
            <div class="answer-button__container" v-if="showConfirm">
                <div class="answer-button__wrapper">
                    <button class="answer-button__button answer-button__bottom-button"
                        @click="onConfirm(selectedAnswer)">
                        <div v-if="chatMessage.options.makeModelChatQuestionType === MakeModelChatQuestionType.Colour">
                            {{ returnColourText() }}</div>
                        <div v-if="chatMessage.options.makeModelChatQuestionType !== MakeModelChatQuestionType.Colour">
                            Confirm</div>
                    </button>
                </div>
                <div class="answer-button__wrapper">
                    <button v-if="chatMessage.options.makeModelChatQuestionType !== MakeModelChatQuestionType.Colour"
                        class="answer-button__button" @click="onCancel()">
                        <div>
                            Cancel</div>
                    </button>
                </div>
            </div>
        </div>
        <div v-if="showBot">
            <make-model-bot-chat :question="chatMessage.options.text" :conversationId="conversationId"
                @onChatFinished="onChatFinished()"></make-model-bot-chat>
        </div>
    </div>
</template>

<script lang="ts">
import MakeModelChatQuestionType from '@/enums/MakeModelChatQuestionType';
import ChatMessage from '@/models/vehicle/makeModelBot/chatMessage';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import MakeModelChatType from '@/enums/MakeModelChatType';
import ColourPickerComponent from './ColourPickerComponent.vue';
import ChatBotAnswerSupportComponent from './ChatBotAnswerSupportComponent.vue';
import ChatMessageComponent from './ChatMessageComponent.vue';

@Component({
    components: {
        'colour-picker-component': ColourPickerComponent,
        'make-model-bot-chat': ChatBotAnswerSupportComponent,
        'chat-message-component': ChatMessageComponent
    }
})

export default class AnswerButtonsComponent extends Vue {
    @Prop({ default: '' })
    public chatMessage!: ChatMessage;

    @Prop({ default: '' })
    public conversationId!: String;

    public messageInsight: ChatMessage = new ChatMessage(MakeModelChatType.BotMessage);

    public disableAnswerButtons: boolean = false;

    private showConfirm: boolean = false;

    private selectedAnswer: string = '';

    private showBot: boolean = false;

    private MakeModelChatQuestionType = MakeModelChatQuestionType;

    private helpButtonSelected: boolean = false;

    private notSure: boolean = false;

    public onSelect(answer: string) {
        if (this.chatMessage.options.makeModelChatQuestionType !== MakeModelChatQuestionType.Colour) {
            this.disableAnswerButtons = true;
        }
        if (this.chatMessage.options?.bypassConfirmation) {
            this.$emit('onMakeModelAnswerSelected', answer);
            this.selectedAnswer = answer;
            return;
        }

        this.showConfirm = true;
        this.selectedAnswer = answer;
    }

    private getButtonClass(index: number) {
        let isLastButton = false;
        if (this.chatMessage.options.choices) {
            isLastButton = index === this.chatMessage.options.choices?.length - 1;
        }

        const hasHelpButton = this.chatMessage.options.helpOptions?.helpButton;

        return {
            'answer-button__bottom-button': !hasHelpButton && isLastButton,
            'answer-button__standard-button': hasHelpButton || (!hasHelpButton && !isLastButton)
        };
    }

    private returnColourText(): string {
        return this.notSure ? 'I\'ll choose later' : 'Choose this colour';
    }

    public onConfirm(answer: string) {
        if (this.chatMessage.options.makeModelChatQuestionType === MakeModelChatQuestionType.Colour) {
            this.disableAnswerButtons = true;
        }
        this.showConfirm = false;
        this.$emit('onMakeModelAnswerSelected', answer);
    }

    public onCancel() {
        this.showConfirm = false;
        this.selectedAnswer = '';
        this.disableAnswerButtons = false;
    }

    public onHelpRequested() {
        this.helpButtonSelected = true;
        this.disableAnswerButtons = true;
        if (this.chatMessage.options.helpOptions!.helpRequested === true) {
            this.showBot = true;
        }
        this.$emit('onMakeModelHelpSelected');
    }

    public onChatBotRequested() {
        this.disableAnswerButtons = true;
        this.showBot = true;
    }

    public onChatFinished() {
        this.$emit('onChatFinished');
    }

    private onAnswerChange(notSure: boolean) {
        this.notSure = notSure;
    }

    public reset() {
        this.disableAnswerButtons = false;
    }

    public mounted() {
        this.chatMessage.isFinished = true;
        this.$events.on('formReset', this.reset);
    }
}
</script>
